import { Container } from 'pixi.js';
import type { Application } from 'pixi.js';

// Design-space resolution. SceneManager scales a root container to fit the
// real window inside this logical viewport (letterboxed) and every scene's
// resize() is always called with these exact numbers.
export const LOGICAL_WIDTH = 1280;
export const LOGICAL_HEIGHT = 720;

export interface Scene {
  /** Stable name exposed on document.body.dataset.scene for automation (class names get minified). */
  name?: string;
  container: Container;
  enter(): void | Promise<void>;
  exit(): void;
  update(dt: number): void;
  resize(w: number, h: number): void;
}

// Owns the Pixi root container, the letterbox scale/position math and the
// current Scene's lifecycle (enter/exit/update/resize). One SceneManager per
// Application.
export class SceneManager {
  // Exposed (read-only) so scenes that need real Pixi rendering beyond the
  // scene graph - RaceScene painting skid marks into a RenderTexture, in
  // particular - can reach the renderer without every Scene constructor
  // having to thread it through separately.
  readonly app: Application;
  private readonly root: Container;
  private current: Scene | null = null;

  constructor(app: Application) {
    this.app = app;
    this.root = new Container();
    this.app.stage.addChild(this.root);

    this.app.renderer.on('resize', () => this.applyLayout());
    this.applyLayout();

    this.app.ticker.add((ticker) => {
      this.current?.update(ticker.deltaMS / 1000);
    });
  }

  async go(scene: Scene): Promise<void> {
    const previous = this.current;
    this.current = scene;

    this.root.addChild(scene.container);
    scene.resize(LOGICAL_WIDTH, LOGICAL_HEIGHT);
    await scene.enter();
    // Exposed for automated screenshots and tests: which scene is live and ready.
    document.body.dataset['scene'] = scene.name ?? scene.constructor.name;

    if (previous) {
      previous.exit();
      this.root.removeChild(previous.container);
    }
  }

  private applyLayout(): void {
    const w = this.app.renderer.width;
    const h = this.app.renderer.height;
    const scale = Math.min(w / LOGICAL_WIDTH, h / LOGICAL_HEIGHT);

    this.root.scale.set(scale);
    this.root.x = (w - LOGICAL_WIDTH * scale) / 2;
    this.root.y = (h - LOGICAL_HEIGHT * scale) / 2;

    this.current?.resize(LOGICAL_WIDTH, LOGICAL_HEIGHT);
  }
}
